import { c as _c } from "react/compiler-runtime";
import { useCompanyId } from "@context-providers/company-id-provider";
import { areActiveMembersOutdatedSelector, isRefreshingActiveMembersSelector } from "@store/active-members/active-members-selector";
import { resetActiveMembersState } from "@store/active-members/active-members-slice";
import { fetchActiveMembers, refreshActiveMembers } from "@store/active-members/active-members-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { sentryCaptureError } from "@utils/sentry-utils";
import { useEffect } from "react";

/**
 * Fetches the active members of the selected workspace into the store when the calling component
 * mounts or the workspace changes, and clears them again when it unmounts.
 *
 * Clearing them also makes the store ignore a response that arrives afterwards, so that it neither
 * shows the members of the workspace the user left nor keeps the next fetch from running. Users
 * that may not read them get a rejected request that leaves them empty.
 *
 * While they are shown, they are fetched again in the background whenever they became outdated,
 * e.g. since a seat or a workspace role of a member changed. Only one refresh runs at a time, and
 * the members that became outdated meanwhile are refreshed once it is done.
 *
 * @param shouldFetch Whether to fetch the active members at all
 */
export function useFetchActiveMembers(shouldFetch) {
  const $ = _c(12);
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const isOutdated = useAppSelector(areActiveMembersOutdatedSelector);
  const isRefreshing = useAppSelector(isRefreshingActiveMembersSelector);
  let t0;
  let t1;
  if ($[0] !== companyId || $[1] !== dispatch || $[2] !== shouldFetch) {
    t0 = () => {
      if (!shouldFetch) {
        return;
      }
      dispatch(fetchActiveMembers({
        companyId
      }));
      return () => {
        dispatch(resetActiveMembersState());
      };
    };
    t1 = [companyId, dispatch, shouldFetch];
    $[0] = companyId;
    $[1] = dispatch;
    $[2] = shouldFetch;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[5] !== companyId || $[6] !== dispatch || $[7] !== isOutdated || $[8] !== isRefreshing || $[9] !== shouldFetch) {
    t2 = () => {
      if (!shouldFetch || !isOutdated || isRefreshing) {
        return;
      }
      dispatch(refreshActiveMembers({
        companyId
      })).then(_temp);
    };
    t3 = [companyId, dispatch, isOutdated, isRefreshing, shouldFetch];
    $[5] = companyId;
    $[6] = dispatch;
    $[7] = isOutdated;
    $[8] = isRefreshing;
    $[9] = shouldFetch;
    $[10] = t2;
    $[11] = t3;
  } else {
    t2 = $[10];
    t3 = $[11];
  }
  useEffect(t2, t3);
}
function _temp(action) {
  if (refreshActiveMembers.rejected.match(action) && !action.meta.aborted && !action.meta.condition) {
    sentryCaptureError({
      error: action.error,
      title: "Cannot refresh the active members"
    });
  }
}